import { Account, UpdateAccount, UploadImage } from "../interfaces/account";
import axiosInstance from "./axios-instance";
import { Config } from "../utils";
import { uploadFileWithPresign } from "../helper/presignedUpload";
import { cloneCacheValue, isMutationSuccess } from "./cache-utils";
import { invalidatePlanInfoCache } from "./stripe";

export type SubscriptionPlanVariant = 'old' | 'new';

const getScopedAccountId = (accountId?: number): number => {
    if (typeof window === "undefined") {
        return Number(accountId || 0);
    }
    const ownerAccountId = Number(localStorage.getItem("ownerAccountId") || "0");
    if (ownerAccountId > 0) {
        return ownerAccountId;
    }
    if (Number(accountId) > 0) {
        return Number(accountId);
    }
    return Number(localStorage.getItem("accountId") || "0");
};

const parseFormDataAccountId = (payload: FormData): number => {
    const raw = payload.get("accountId");
    if (typeof raw === "string") {
        const parsed = Number(raw);
        if (Number.isFinite(parsed)) {
            return parsed;
        }
    }
    return 0;
};

// Dedupe layer for /account/get. Many components fetch the same account on
// page load, often within milliseconds of each other. Two tiers:
//  1) in-flight: if a fetch for accountId X is already running, the second
//     caller awaits the same promise (no second HTTP request)
//  2) short result cache: 5s TTL keyed by accountId; absorbs follow-up calls
//     that come in after a request completes
// updateAccount() invalidates both.
const ACCOUNT_RESULT_TTL_MS = 5000;
const inFlightAccount = new Map<number, Promise<Account>>();
const resultAccount = new Map<number, { data: Account; fetchedAt: number }>();

const getAccount = async (accountId: number): Promise<Account> => {
    const cached = resultAccount.get(accountId);
    if (cached && Date.now() - cached.fetchedAt < ACCOUNT_RESULT_TTL_MS) {
        return cloneCacheValue(cached.data);
    }
    const inFlight = inFlightAccount.get(accountId);
    if (inFlight) {
        return cloneCacheValue(await inFlight);
    }
    const promise = (async () => {
        try {
            const response = await axiosInstance.get<Account>(
                `/account/get?accountId=${accountId}`
            );
            resultAccount.set(accountId, { data: cloneCacheValue(response.data), fetchedAt: Date.now() });
            return response.data;
        } catch (error) {
            console.error("Error getting account data:", error);
            throw error;
        } finally {
            inFlightAccount.delete(accountId);
        }
    })();
    inFlightAccount.set(accountId, promise);
    return cloneCacheValue(await promise);
};

const invalidateAccountCache = (accountId?: number): void => {
    if (accountId === undefined) {
        inFlightAccount.clear();
        resultAccount.clear();
        return;
    }
    inFlightAccount.delete(accountId);
    resultAccount.delete(accountId);
};

const updateAccount = async (payload: UpdateAccount) => {
    try {
        const response = await axiosInstance.put<UpdateAccount>(
            "/account/update",
            payload
        );
        // Account data just changed; drop the dedupe cache so the next
        // getAccount() picks up fresh server state.
        const idCandidate = (payload as any)?.accountId;
        if (isMutationSuccess(response.data) && typeof idCandidate === "number" && idCandidate > 0) {
            invalidateAccountCache(idCandidate);
        } else if (isMutationSuccess(response.data)) {
            invalidateAccountCache();
        }
        return response.data;
    } catch (error) {
        console.error("Error with account update:", error);
        throw error;
    }
};

const uploadImage = async (payload: FormData) => {
    try {
        const scopedAccountId = getScopedAccountId(parseFormDataAccountId(payload));
        if (scopedAccountId > 0) {
            payload.set("accountId", String(scopedAccountId));
        }
        const file = payload.get("file");
        if (!(file instanceof Blob)) {
            throw new Error("Missing file");
        }

        if (file.size > 10 * 1024 * 1024) {
            throw new Error("Maximum size is 10 MB");
        }

        if (Config.fileStoreUrl.includes("localhost")) {
            const response = await axiosInstance.put<UploadImage>(
                "account/upload_image",
                payload,
                {
                    headers: { "Content-Type": "multipart/form-data" },
                }
            );
            return response.data;
        }

        const remoteInfo = await uploadFileWithPresign({
            accountId: scopedAccountId,
            file,
            fileName: typeof File !== "undefined" && file instanceof File ? file.name : "upload.png",
            contentType: file.type || "image/png",
            uploadType: "image",
        });

        return { url: remoteInfo.remoteSource };
    } catch (error) {
        console.error("Error with account upload image:", error);
        throw error;
    }
};

const getFileNameFromRemoteImageUrl = (imageUrl: string, contentType: string): string => {
    const extensionByType: Record<string, string> = {
        "image/jpeg": "jpg",
        "image/jpg": "jpg",
        "image/png": "png",
        "image/webp": "webp",
        "image/gif": "gif",
        "image/svg+xml": "svg",
    };

    try {
        const url = new URL(imageUrl);
        const nameFromPath = url.pathname.split("/").filter(Boolean).pop();
        if (nameFromPath && /\.[a-z0-9]+$/i.test(nameFromPath)) {
            return nameFromPath;
        }
    } catch (_error) {
        // Fall back to the content type below.
    }

    const extension = extensionByType[contentType.toLowerCase()] || "jpg";
    return `fabric-url-image.${extension}`;
};

const uploadRemoteImage = async (imageUrl: string, accountId?: number) => {
    const response = await fetch(imageUrl);
    if (!response.ok) {
        throw new Error(`Unable to download image. Status: ${response.status}`);
    }

    const blob = await response.blob();
    if (!blob.type || !blob.type.toLowerCase().startsWith("image/")) {
        throw new Error("Remote URL did not return an image.");
    }

    const scopedAccountId = getScopedAccountId(accountId);
    const fileName = getFileNameFromRemoteImageUrl(imageUrl, blob.type);
    const formData = new FormData();
    formData.append("file", blob, fileName);
    formData.append("accountId", String(scopedAccountId));
    formData.append("fileStoreUrl", Config.fileStoreUrl);

    return uploadImage(formData);
};

const getPlans = async (accountId: number) => {
    try {
        const response = await axiosInstance.put<any>("account/get_plans", {
            accountId,
        });
        return response.data;
    } catch (error) {
        console.error("Error with account get plans:", error);
        throw error;
    }
};

const subscribeAccount = async (payload: {
    id: number;
    paymentMethod?: string;
    planType: string;
    couponCode?: string | undefined;
    isFirstSubscribe?: boolean;
}) => {
    try {
        const response = await axiosInstance.put<any>("account/subscribe", {
            payload,
        });
        if (isMutationSuccess(response.data)) {
            invalidateAccountCache(payload.id);
            invalidatePlanInfoCache(payload.id);
        }
        return response.data;
    } catch (error) {
        console.error("Error with account subscribe:", error);
        throw error;
    }
};

const changeSubscriptionPlan = async (
    accountId: number,
    variant: SubscriptionPlanVariant = 'old'
) => {
    try {
        const response = await axiosInstance.post(
            `/stripe/change-subscription-plan?accountId=${accountId}&variant=${variant}`
        );
        invalidateAccountCache(accountId);
        invalidatePlanInfoCache(accountId);
        return response.data;
    } catch (error) {
        console.log(error);
        return { error };
    }
};

const changeCard = async (accountId: number) => {
    try {
        const response = await axiosInstance.post(
            `/stripe/change-card?accountId=${accountId}`
        );
        return response.data;
    } catch (error) {
        console.log(error);
        return { error };
    }
};

const retrieveMetadata = async (accountId: number) => {
    try {
        const scopedAccountId = getScopedAccountId(accountId);
        const response = await axiosInstance.get(
            `/stripe/retrieve-metadata?accountId=${scopedAccountId}`
        );
        return response.data;
    } catch (error) {
        console.log(error);
        return { error };
    }
};

const buyExtraOrders = async (accountId: number) => {
    try {
        const response = await axiosInstance.post(
            `/stripe/buy-more-orders`, { accountId }
        )
        invalidateAccountCache(accountId);
        invalidatePlanInfoCache(accountId);
        return response.data;
    } catch (error){
        console.log(error);
        return { error };
    }
}

const buyExtraSeats = async (accountId: number) => {
    try {
        const response = await axiosInstance.post(
            `/stripe/buy-more-seats`, { accountId }
        )
        invalidateAccountCache(accountId);
        invalidatePlanInfoCache(accountId);
        return response.data;
    } catch (error){
        console.log(error);
        return { error };
    }
}

const getSeatInfo = async (accountId: number) => {
    try {
        const response = await axiosInstance.get(
            `/stripe/get-seats-info`,
            {
              params: {
                accountId: accountId
              }
            }
        )
        return response.data;
    } catch (error){
        console.log(error);
        return { error };
    }
}

const buyAdditionalSeats = async (payload: any) => {
    try {
        const response = await axiosInstance.post(
            `/stripe/buy-additional-seats`, payload
        )
        invalidateAccountCache(payload?.accountId);
        invalidatePlanInfoCache(payload?.accountId);
        return response.data;
    } catch (error){
        console.log(error);
        return { error };
    }
}

const updateAdditionalSeats = async (payload: any) => {
    try {
        const response = await axiosInstance.post(
            `/stripe/update-additional-seats`, payload
        )
        invalidateAccountCache(payload?.accountId);
        invalidatePlanInfoCache(payload?.accountId);
        return response.data;
    } catch (error){
        console.log(error);
        return { error };
    }
}

export {
    getAccount,
    invalidateAccountCache,
    updateAccount,
    uploadImage,
    uploadRemoteImage,
    getPlans,
    subscribeAccount,
    changeSubscriptionPlan,
    retrieveMetadata,
    changeCard,
    buyExtraOrders,
    buyExtraSeats,
    getSeatInfo,
    buyAdditionalSeats,
    updateAdditionalSeats
};
